import { Card } from '@chakra-ui/react';
import {
  ContactTypes,
  IContactRespSchema,
  IQuoteRequestSchema,
  QuoteStatusEnum,
  SigningOrderEnum,
} from '@monetize/types/app';
import { FunctionComponent as FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useUpdateQuoteEsignRequirement } from '../../../../../api/cpqService';
import {
  MBox,
  MCard,
  MDivider,
  MFlex,
  MRadio,
  MRadioGroup,
  MStack,
  MSwitch,
  MText,
  MVStack,
} from '../../../../../components/Monetize';
import { useQuoteContext } from '../../quoteContext';
import {
  getCanChangeLinkedAccount,
  shouldAllowEsignEdit,
} from '../../quoteUtils';
import { QuoteContactAdditionalRecipients } from './QuoteContactAdditionalRecipients';
import { QuoteContactDrawerSelect } from './QuoteContactDrawerSelect';

interface QuoteContactsDrawerEsignProps {
  canUpdateContact: boolean;
  canCreateContact: boolean;
  onChangeSigningOrder: (signingOrder: SigningOrderEnum) => void;
}

export const QuoteContactsDrawerEsign: FC<QuoteContactsDrawerEsignProps> = ({
  canUpdateContact,
  canCreateContact,
  onChangeSigningOrder,
}: QuoteContactsDrawerEsignProps) => {
  const {
    quoteContacts: {
      quoteContacts,
      internalAccountContacts,
      additionalRecipientSelectedContacts,
      handleSelectedAdditionalRecipients,
      handleCreatedNewAdditionalContact,
      handleAdditionalContactRemove,
      additionalSigners,
      handleAdditionalSignerRemove,
      handleAdditionalSignerAdd,
      handleAdditionalSignerSelect,
      isDisabled,
      isDisabledEsign,
      handleChangeContact,
      esignCustomerAccountId,
      handleEsignCustomerAccountIdChange,
      handleResetEsignContacts,
      linkedAccount,
      accountSelectItems,
    },
    quoteData: { quote },
    quoteSettingsData: { quoteSettings },

    isReadOnlyQuote,
  } = useQuoteContext();
  const esignContact = quoteContacts.esign.find((contact) => !contact.internal);
  const esignInternalContact = quoteContacts.esign.find(
    (contact) => contact.internal,
  );
  const { allowEsignEditOnQuoteReview } = quoteSettings || {};

  const updateEsignRequirement = useUpdateQuoteEsignRequirement();
  const canChangeSecondaryAccount = getCanChangeLinkedAccount(quote!);

  const { control, watch } = useFormContext<IQuoteRequestSchema>();
  const requiresEsignWatch = watch('requiresEsign');

  const handleChangeContactAndEvaluate = async (
    contact: IContactRespSchema,
    type: ContactTypes,
    isInternal: boolean,
  ) => {
    handleChangeContact(contact, type, isInternal);
  };

  const isEsignControlReadOnlyOrDisabled = () => {
    return shouldAllowEsignEdit(quote?.status, allowEsignEditOnQuoteReview)
      ? false
      : isReadOnlyQuote || quote?.status !== QuoteStatusEnum.DRAFT;
  };

  if (!quote) {
    return null;
  }

  const { linkedAccountName, accountName, signingOrder } = quote;
  const legalEntityName = quote?.fromCompany.name;

  const alreadySelectedContacts = quoteContacts.esign.filter(
    (c) => !c.internal,
  );

  return (
    <>
      <MCard variant="borderless" mb="2">
        <Card.Body py="2">
          <MFlex alignItems="center" justify="space-between">
            <MBox>
              <MText fontSize="sm" fontWeight="600">
                eSign
              </MText>
              <MText my="1" fontSize="sm">
                Enable eSign for this quote.
              </MText>
            </MBox>

            <Controller
              name="requiresEsign"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <MSwitch
                  isReadOnly={isEsignControlReadOnlyOrDisabled()}
                  isDisabled={isEsignControlReadOnlyOrDisabled()}
                  isChecked={value}
                  onChange={(e) => {
                    onChange(e.target.checked);
                    if (!e.target.checked) {
                      handleResetEsignContacts();
                    }
                    updateEsignRequirement.mutate({
                      quoteId: quote?.id ?? '',
                      requiresEsign: e.target.checked,
                    });
                  }}
                  {...rest}
                />
              )}
            />
          </MFlex>
        </Card.Body>
      </MCard>
      {requiresEsignWatch && (
        <>
          <MCard variant="borderless" mb="2">
            <Card.Body>
              <MRadioGroup
                onChange={(newValue) => {
                  onChangeSigningOrder &&
                    newValue &&
                    onChangeSigningOrder(newValue as SigningOrderEnum);
                }}
                value={signingOrder}
                isDisabled={isDisabled}
              >
                <MStack columnGap={5}>
                  <MRadio value={SigningOrderEnum.EXTERNAL_FIRST}>
                    Send to{' '}
                    <strong>
                      {linkedAccountName ? linkedAccountName : accountName}
                    </strong>{' '}
                    first
                  </MRadio>
                  <MRadio value={SigningOrderEnum.INTERNAL_FIRST}>
                    Send to{' '}
                    <strong>{legalEntityName || 'internal contact'}</strong>{' '}
                    first
                  </MRadio>
                  <MRadio value={SigningOrderEnum.INSTANTANEOUS}>
                    Send to both contacts at the same time
                  </MRadio>
                  <MRadio value={SigningOrderEnum.EXTERNAL_ONLY}>
                    Only require signature from customer{' '}
                    <strong>
                      {linkedAccountName ? linkedAccountName : accountName}
                    </strong>{' '}
                  </MRadio>
                </MStack>
              </MRadioGroup>
            </Card.Body>
          </MCard>

          <MCard variant="borderless" mb="2">
            <Card.Body py="2">
              <MVStack gap={2}>
                {quote?.signingOrder !== SigningOrderEnum.EXTERNAL_ONLY && (
                  <>
                    <QuoteContactDrawerSelect
                      label="eSign Internal Contact"
                      value={esignInternalContact}
                      onChange={(contact) =>
                        handleChangeContactAndEvaluate(contact, 'esign', true)
                      }
                      contacts={internalAccountContacts}
                      isDisabled={isDisabledEsign}
                      isReadOnly={isDisabledEsign}
                      accountId={quote?.accountId!}
                      canUpdateContact={canUpdateContact}
                      isInternal
                    />
                    <MDivider />
                  </>
                )}
                <QuoteContactDrawerSelect
                  label="eSign Customer Contact"
                  value={esignContact}
                  onChange={(contact) =>
                    handleChangeContactAndEvaluate(contact, 'esign', false)
                  }
                  isDisabled={isDisabledEsign}
                  isReadOnly={isDisabledEsign}
                  canUpdateContact={canUpdateContact}
                  handleChangeAccount={handleEsignCustomerAccountIdChange}
                  accountId={esignCustomerAccountId!}
                  accountSelectLabel="eSign Customer Account"
                  accountSelectItems={accountSelectItems}
                  showAccountSelect={!!linkedAccount}
                  isAccountSelectDisabled={!canChangeSecondaryAccount}
                />
              </MVStack>
            </Card.Body>
          </MCard>

          <QuoteContactAdditionalRecipients
            label="Additional Signers"
            isDisabled={isDisabled}
            quoteAccountId={quote?.accountId!}
            selectedContacts={additionalSigners}
            selectedExternalEsignContacts={[
              ...alreadySelectedContacts,
              ...additionalRecipientSelectedContacts,
            ]}
            onSelectedContactsChange={handleAdditionalSignerSelect}
            handleCreatedNewContact={handleAdditionalSignerAdd}
            onRemoveAdditionalContact={handleAdditionalSignerRemove}
            lengthLimit={1}
            isAccountSelectDisabled={!canChangeSecondaryAccount}
            accountSelectItems={accountSelectItems}
            showAccountSelect={!!linkedAccount}
          />

          <QuoteContactAdditionalRecipients
            label="Additional Recipients"
            isDisabled={isDisabled}
            quoteAccountId={quote?.accountId!}
            selectedContacts={additionalRecipientSelectedContacts}
            selectedExternalEsignContacts={[
              ...alreadySelectedContacts,
              ...additionalSigners,
            ]}
            onSelectedContactsChange={handleSelectedAdditionalRecipients}
            handleCreatedNewContact={handleCreatedNewAdditionalContact}
            onRemoveAdditionalContact={handleAdditionalContactRemove}
            isAccountSelectDisabled={!canChangeSecondaryAccount}
            accountSelectItems={accountSelectItems}
            showAccountSelect={!!linkedAccount}
          />
        </>
      )}
    </>
  );
};
